import { OAUTH, type OAuthProvider } from "@oshimap/config";
import { z } from "zod";
import {
  placeSchema, mapMarkerSchema, eventSchema, passportSchema,
  visitResultSchema, currentUserSchema, favoriteSchema, stampSchema, visitRecordSchema,
  authResultSchema, reportSchema, interestSchema, recommendationSchema, nearbyPayloadSchema,
  notificationSchema, shareCardSchema, liveUpdateSchema,
} from "@oshimap/schemas";
import type { Bounds, LatLng } from "@oshimap/domain";
import type { ApiClient, RequestOptions } from "./client";

const arr = <T extends z.ZodTypeAny>(t: T) => z.array(t);

/**
 * Legacy /api/v1 을 그대로 쓴다. 계약을 바꾸지 않는다 (13. Backend 원칙).
 * 경로와 파라미터는 09_API_CONTRACT 실측을 따른다.
 */
export function endpoints(api: ApiClient) {
  const opt = (o?: Partial<RequestOptions>): RequestOptions => ({ anonymous: true, ...o });

  return {
    /* ---- Layer 1: Discover (로그인 불필요) ---- */
    listPlaces: (q: { search?: string; category?: string; status?: string; tagId?: string; pageSize?: number } = {}, o?: RequestOptions) =>
      api.request("/places", arr(placeSchema), { ...opt(o), query: { ...q, pageSize: q.pageSize ?? 50 } }),

    getPlace: (id: string, o?: RequestOptions) =>
      api.request(`/places/${id}`, placeSchema, opt(o)),

    listTags: (o?: RequestOptions) =>
      api.request("/places/tags", arr(z.object({ id: z.string(), name: z.string() })), opt(o)),

    /** bounds 모드. nearby 와 동시 사용 시 서버가 400 을 준다. */
    mapItemsByBounds: (b: Bounds, q: { targetType?: "PLACE" | "EVENT"; category?: string; eventStatus?: string } = {}, o?: RequestOptions) =>
      api.request("/map/items", arr(mapMarkerSchema), {
        ...opt(o),
        query: { north: b.north, south: b.south, east: b.east, west: b.west, ...q },
      }),

    mapItemsNearby: (p: LatLng, radiusKm: number, q: { targetType?: "PLACE" | "EVENT" } = {}, o?: RequestOptions) =>
      api.request("/map/items", arr(mapMarkerSchema), {
        ...opt(o),
        query: { lat: p.latitude, lng: p.longitude, radius: radiusKm, ...q },
      }),

    listEvents: (q: { search?: string } = {}, o?: RequestOptions) =>
      api.request("/events", arr(eventSchema), { ...opt(o), query: q }),

    getEvent: (id: string, o?: RequestOptions) =>
      api.request(`/events/${id}`, eventSchema, opt(o)),

    /* ---- Layer 2: Keep (로그인 필요) ---- */
    /** 서버가 latitude/longitude/radius(미터, 100~50000) 를 요구한다. */
    nearbyPlaces: (p: LatLng, radiusMeters = 3000, q: { category?: string; tagId?: string } = {}, o?: RequestOptions) =>
      api.request("/places/nearby", nearbyPayloadSchema, {
        ...o, anonymous: false,
        query: { latitude: p.latitude, longitude: p.longitude, radius: radiusMeters, ...q },
      }),

    /* ---- 관심 대상 (Oshi) ---- */
    listInterests: (o?: RequestOptions) =>
      api.request("/interests", arr(interestSchema), { ...o, anonymous: false }),

    createInterest: (body: {
      tagId?: string; categoryCode?: string; region?: string;
      notifyNewPlaces?: boolean; notifyEventDeadlines?: boolean; notifyStockUpdates?: boolean;
    }, o?: RequestOptions) =>
      api.request("/interests", interestSchema, { ...o, method: "POST", body, anonymous: false }),

    deleteInterest: (id: string, o?: RequestOptions) =>
      api.request(`/interests/${id}`, z.object({ deleted: z.boolean() }), { ...o, method: "DELETE", anonymous: false }),

    /** 규칙 기반 추천. AI 를 쓰지 않는다 (P10). */
    recommendations: (o?: RequestOptions) =>
      api.request("/interests/recommendations", arr(recommendationSchema), { ...o, anonymous: false }),

    listFavorites: (o?: RequestOptions) =>
      api.request("/favorites", arr(favoriteSchema), { ...o, anonymous: false }),

    addFavorite: (targetType: "PLACE" | "EVENT", targetId: string, o?: RequestOptions) =>
      api.request("/favorites", favoriteSchema, { ...o, method: "POST", body: { targetType, targetId }, anonymous: false }),

    removeFavorite: (targetType: "PLACE" | "EVENT", targetId: string, o?: RequestOptions) =>
      api.requestRaw(`/favorites/${targetType}/${targetId}`, { ...o, method: "DELETE", anonymous: false }),

    /* ---- Layer 3: Collect ---- */
    getPassport: (o?: RequestOptions) =>
      api.request("/passport", passportSchema, { ...o, anonymous: false }),

    getPassportStamps: (o?: RequestOptions) =>
      api.request("/passport/stamps", arr(stampSchema), { ...o, anonymous: false }),

    getPassportTimeline: (o?: RequestOptions) =>
      api.request("/passport/timeline", arr(visitRecordSchema), { ...o, anonymous: false }),

    /** 응답의 awardedStamps 가 STAMP Motion 트리거다. */
    createVisit: (body: { placeId?: string; eventId?: string; visitedOn: string; memo?: string; rating?: number; visibility?: string }, o?: RequestOptions) =>
      api.request("/visits", visitResultSchema, { ...o, method: "POST", body, anonymous: false }),

    /* ---- 제보 (P6: 승인 전에는 원본을 덮어쓰지 않는다) ---- */
    /* ---- 현장 정보 (Live) ---- */

    /** 비로그인도 볼 수 있다 — 가기 전에 확인하는 정보라 로그인 뒤로 숨기면 뜻이 없다. */
    listPlaceLive: (placeId: string, o?: RequestOptions) =>
      api.request(`/live-updates/places/${placeId}`, arr(liveUpdateSchema), opt(o)),

    createLive: (body: { placeId?: string; eventId?: string; type: string; value?: Record<string, unknown> }, o?: RequestOptions) =>
      api.request("/live-updates", liveUpdateSchema, { ...o, method: "POST", body, anonymous: false }),

    /** 실측: CONFIRM / DISPUTE / OUTDATED 셋만 받는다. 한 사람당 하나로 덮어쓴다. */
    confirmLive: (id: string, confirmation: "CONFIRM" | "DISPUTE" | "OUTDATED", o?: RequestOptions) =>
      api.request(`/live-updates/${id}/confirm`, liveUpdateSchema, { ...o, method: "POST", body: { confirmation }, anonymous: false }),

    withdrawLive: (id: string, o?: RequestOptions) =>
      api.request(`/live-updates/${id}/withdraw`, liveUpdateSchema, { ...o, method: "POST", anonymous: false }),

    listReports: (o?: RequestOptions) =>
      api.request("/reports", arr(reportSchema), { ...o, anonymous: false }),

    getReport: (id: string, o?: RequestOptions) =>
      api.request(`/reports/${id}`, reportSchema, { ...o, anonymous: false }),

    /** clientRequestId 는 서버 필수다 — 멱등키이므로 재시도해도 중복 제보가 생기지 않는다. */
    createReport: (body: {
      clientRequestId: string; reportType: string; title: string; summary?: string;
      targetPlaceId?: string; targetEventId?: string;
      changes?: Array<{ fieldPath: string; proposedValue: unknown }>;
      sourceLinks?: Array<{ platform: string; url: string; label?: string; isOfficial?: boolean }>;
    }, o?: RequestOptions) =>
      api.request("/reports", reportSchema, { ...o, method: "POST", body, anonymous: false }),

    submitReport: (id: string, o?: RequestOptions) =>
      api.request(`/reports/${id}/submit`, reportSchema, { ...o, method: "POST", anonymous: false }),

    withdrawReport: (id: string, o?: RequestOptions) =>
      api.request(`/reports/${id}/withdraw`, reportSchema, { ...o, method: "POST", anonymous: false }),

    /* ---- Auth (Legacy /api/v1/auth 그대로 사용) ---- */
    login: (email: string, password: string, o?: RequestOptions) =>
      api.request("/auth/login", authResultSchema, { ...opt(o), method: "POST", body: { email, password } }),

    checkEmail: (email: string, o?: RequestOptions) =>
      api.request("/auth/check-email", z.object({ available: z.boolean() }), { ...opt(o), query: { email } }),

    /** 6자리 코드 발송. 개발 환경에서는 devCode 가 함께 온다. */
    requestEmailVerification: (email: string, o?: RequestOptions) =>
      api.request("/auth/email-verifications",
        z.object({ verificationId: z.string(), devCode: z.string().optional() }),
        { ...opt(o), method: "POST", body: { email } }),

    confirmEmailVerification: (verificationId: string, code: string, o?: RequestOptions) =>
      api.request("/auth/email-verifications/confirm",
        z.object({ verificationToken: z.string() }),
        { ...opt(o), method: "POST", body: { verificationId, code } }),

    register: (input: { email: string; password: string; passwordConfirm: string; displayName: string; verificationToken: string }, o?: RequestOptions) =>
      api.request("/auth/register", authResultSchema, { ...opt(o), method: "POST", body: input }),

    /**
     * 소셜 로그인 마무리. 제공자에서 돌아온 직후 한 번만 성공한다 —
     * 서버가 쿠키 세션에서 값을 pop 하기 때문에 재시도해도 같은 결과가 나오지 않는다.
     * 쿠키를 보내야 하므로 credentials 가 필요하다.
     */
    listNotifications: (o?: RequestOptions) =>
      api.request("/notifications", arr(notificationSchema), { ...o, anonymous: false }),

    readNotification: (id: string, o?: RequestOptions) =>
      api.request(`/notifications/${id}/read`, z.object({ read: z.boolean() }), { ...o, method: "POST", anonymous: false }),

    readAllNotifications: (o?: RequestOptions) =>
      api.request("/notifications/read-all", z.object({ read: z.number() }), { ...o, method: "POST", anonymous: false }),

    oauthSession: (provider: OAuthProvider, o?: RequestOptions) =>
      api.request(OAUTH.session(provider), authResultSchema, { ...opt(o), anonymous: true }),

    me: (o?: RequestOptions) =>
      api.request("/auth/me", currentUserSchema, { ...o, anonymous: false }),

    updateMe: (body: { displayName: string }, o?: RequestOptions) =>
      api.request("/auth/me", currentUserSchema, { ...o, method: "PATCH", body, anonymous: false }),

    /** 실측: 계정을 지우지 않고 WITHDRAWN 으로 바꾸고 식별 정보를 치환한다. */
    withdrawMe: (o?: RequestOptions) =>
      api.request("/auth/me", z.object({ withdrawn: z.boolean() }), { ...o, method: "DELETE", anonymous: false }),

    getShareCard: (o?: RequestOptions) =>
      api.request("/passport/share-card", shareCardSchema, { ...o, anonymous: false }),

    setPassportVisibility: (visibility: string, o?: RequestOptions) =>
      api.request("/passport/visibility", z.object({ visibility: z.string() }),
        { ...o, method: "PATCH", body: { visibility }, anonymous: false }),

    subscribePush: (body: { endpoint: string; keys: Record<string, string> }, o?: RequestOptions) =>
      api.request("/notifications/subscriptions", z.object({ id: z.string(), status: z.string() }),
        { ...o, method: "POST", body, anonymous: false }),

    logout: (o?: RequestOptions) =>
      api.requestRaw("/auth/logout", { ...o, method: "POST", anonymous: false }),

    health: (o?: RequestOptions) =>
      api.request("/health", z.object({ status: z.string() }), opt(o)),
  };
}
export type Endpoints = ReturnType<typeof endpoints>;
